/**
 * Chrome Web Store listing URL + derived extension ID — single source of
 * truth for every V2M frontend that needs to reach the plugin directly.
 *
 * Originally lived only in `voice2mail_webpage/src/config/` (used by
 * `/welcome`'s best-effort `chrome.runtime.sendMessage(id, ...)` install
 * notification). Promoted here (2026-07-20, alerting audit fix) because
 * `x2mail_app`'s `/plugin-auth` handoff page needs the SAME extension ID to
 * verify — via the plugin's already-existing `chrome.runtime.onMessageExternal`
 * `AUTH_SYNC` handler — whether the extension actually received a token,
 * instead of assuming success the instant Firebase sign-in resolves. Two
 * independent copies of the same literal is exactly the drift risk this
 * module exists to prevent — see `CHROME_EXTENSION_ID`'s own docstring.
 */
export const CHROME_STORE_URL = "https://chromewebstore.google.com/detail/voicetomail-gmail-plugin/cjpkplljchcbddbnoepofbcnilkabbdf";
/**
 * Published Chrome extension ID.
 *
 * Derived from the Web Store URL (its last path segment) instead of being
 * duplicated as a second literal, so the two can never drift independently.
 * Must match the extension's `externally_connectable.matches` allow-list for
 * the calling origin (see `frontend/voice2mail_plugin/manifests/manifest.*.json`,
 * generated from `@voice2mail/shared`'s brand domain registry) — that's what
 * lets a web-platform page call `chrome.runtime.sendMessage(id, ...)` directly
 * without a content-script relay.
 */
export const CHROME_EXTENSION_ID = CHROME_STORE_URL.split("/").filter(Boolean).pop() ?? "";
/**
 * Extension ID for an UNPACKED Chrome dev build (`chrome://extensions` ->
 * "Load unpacked" — the normal QA workflow, see
 * `voice2mail_plugin/builds/README` for the build-folder convention).
 *
 * PK manual-test finding (2026-07-21): an unpacked extension's ID is a hash
 * of the absolute filesystem path it was loaded from, UNLESS the manifest
 * pins a `"key"` — so it is DIFFERENT per machine/checkout, and never equal
 * to `CHROME_EXTENSION_ID` (the published Web Store ID). Probing
 * `CHROME_EXTENSION_ID` from a dev build therefore always reported
 * "unreachable" for a real, installed, working extension — the ack probe
 * wasn't wrong, the ID it targeted was.
 *
 * `manifests/manifest.dev.json` now pins a key deriving exactly this ID —
 * see `voice2mail_plugin/manifests/README.md` for the full derivation and
 * the regeneration procedure (deliberately not a secret: unpacked
 * extensions are never signed with it, only ID-derived from the public
 * half already sitting in the manifest).
 */
export const CHROME_EXTENSION_ID_DEV = "nofnpnljiclkgkfphoimflmhokaleojb";
/**
 * Single choke point for "which extension ID should this build's
 * `chrome.runtime.sendMessage(id, ...)` call target" — callers pass their
 * own already-established per-app dev/prod signal (e.g. x2mail_app's and
 * voice2mail_webpage's own `isDev()`, both `VITE_APP_ENV`-based) rather than
 * this module re-deriving it, so there is exactly one env-detection
 * convention per app, not a second one duplicated in `shared`.
 */
export function resolveChromeExtensionId(isDev) {
    return isDev ? CHROME_EXTENSION_ID_DEV : CHROME_EXTENSION_ID;
}
